import React from 'react';
import { useTranslation } from 'react-i18next';

import Checkbox from 'component/Layout/Form/Checkbox';

import classNames from 'classnames';
import { Feature } from 'config/features';

import {
    useLowDepositAtom,
    usePromoDiscountAtom,
    useReservableAtom,
} from 'hooks/form/filter';
import { useSpecialMeta } from 'hooks/form/meta';
import { useChangedFilterList } from 'hooks/form/useChangedFilterList';
import { useFeature } from 'hooks/useFeature';
import { QaIdentifier } from 'hooks/useQa';

import { SearchParam } from 'types/Search/params';

import styles from '../Filter.module.scss';
import Segment from '../Segment';
import {
    MetaValue,
    useDisabledStatus,
} from '../Segment/__hooks/useDisabledStatus';
import FilterLabel from '../Segment/FilterLabel';

import { getSelected } from './__helper/getSelected';

const Special = ({ disableWhen = MetaValue.NoResult }) => {
    const { t } = useTranslation();
    const [reservableState, setReservableState] = useReservableAtom();
    const [lowDepositState, setLowDepositState] = useLowDepositAtom();
    const [promoDiscountState, setPromoDiscountState] = usePromoDiscountAtom();
    const [specialMeta] = useSpecialMeta();
    const { onToggle, onClear, isFilterChanged } = useChangedFilterList();
    const getDisabledStatus = useDisabledStatus(disableWhen);
    const isFilterPromoDiscountActive = useFeature(
        Feature.FilterPromotionalDiscount
    );
    const headline = t('TRANS-specials');
    const labelWithoutDeposit = t('TRANS-reserve-few-days-without-deposit');
    const labelLowDeposit = t('TRANS-low-deposit');
    const labelPromoDiscount = t('TRANS-promo-discounts');

    const isPromoDiscountDisabled =
        !promoDiscountState &&
        specialMeta?.promoDiscount &&
        getDisabledStatus(specialMeta.promoDiscount);

    const isReservableDisabled = !reservableState
        ? specialMeta?.reservable && getDisabledStatus(specialMeta.reservable)
        : false;

    const isLowDepositDisabled =
        !lowDepositState &&
        specialMeta?.lowDeposit &&
        getDisabledStatus(specialMeta.lowDeposit);

    return (
        <Segment
            withDeleteLink
            onDelete={() => {
                onClear(() =>
                    getSelected(
                        reservableState,
                        lowDepositState,
                        promoDiscountState
                    )
                );
                setReservableState(false);
                setLowDepositState(false);
                setPromoDiscountState(false);
            }}
            headline={{
                text: headline,
            }}
            kiFilterGroupData={{
                refKey: 'Special',
                label: headline,
                multi: true,
            }}
            qaIdentifier={QaIdentifier.Search_Filter_Section_Special}
            filterList={[
                {
                    isSelected: reservableState,
                    filterValue: labelWithoutDeposit,
                    content: (
                        <Checkbox
                            key={SearchParam.ReservablePredicted}
                            input={{
                                id: SearchParam.ReservablePredicted,
                            }}
                            isChecked={reservableState}
                            isOnlyDeselectable={
                                reservableState &&
                                specialMeta?.reservable?.hasCount &&
                                specialMeta?.reservable?.count === 0
                            }
                            onChange={() => {
                                onToggle(SearchParam.ReservablePredicted);
                                setReservableState((current) => !current);
                            }}
                            withUXSelection
                            isDisabled={isReservableDisabled}
                            customClass={{
                                Wrapper: classNames({
                                    [styles.Changed]: isFilterChanged(
                                        SearchParam.ReservablePredicted
                                    ),
                                }),
                            }}
                        >
                            <FilterLabel
                                labelText={labelWithoutDeposit}
                                tooltip={`<b>${t(
                                    'TRANS-reservable-for-few-days'
                                )}</b><p>${t(
                                    'TRANS-show-hotels-with-reservable-offers'
                                )}</p>`}
                                {...specialMeta?.reservable}
                            />
                        </Checkbox>
                    ),
                    kiFilterData: {
                        label: labelWithoutDeposit,
                        isSelectable: !isReservableDisabled,
                        urlKey: SearchParam.ReservablePredicted,
                        value: '1',
                        ...specialMeta?.reservable,
                    },
                },
                {
                    isSelected: lowDepositState,
                    filterValue: labelLowDeposit,
                    content: (
                        <Checkbox
                            key={SearchParam.LowDeposit}
                            input={{
                                id: SearchParam.LowDeposit,
                            }}
                            isDisabled={isLowDepositDisabled}
                            isChecked={lowDepositState}
                            isOnlyDeselectable={
                                lowDepositState &&
                                specialMeta?.lowDeposit?.hasCount &&
                                specialMeta?.lowDeposit?.count === 0
                            }
                            onChange={() => {
                                onToggle(SearchParam.LowDeposit);
                                setLowDepositState((current) => !current);
                            }}
                            withUXSelection
                            customClass={{
                                Wrapper: classNames({
                                    [styles.Changed]: isFilterChanged(
                                        SearchParam.LowDeposit
                                    ),
                                }),
                            }}
                        >
                            <FilterLabel
                                labelText={labelLowDeposit}
                                {...specialMeta?.lowDeposit}
                            />
                        </Checkbox>
                    ),
                    kiFilterData: {
                        label: labelLowDeposit,
                        isSelectable: !isLowDepositDisabled,
                        urlKey: SearchParam.LowDeposit,
                        value: '1',
                        ...specialMeta?.lowDeposit,
                    },
                },
                {
                    isSelected: promoDiscountState,
                    filterValue: labelPromoDiscount,
                    content: !isFilterPromoDiscountActive ? null : (
                        <Checkbox
                            key={SearchParam.PromoDiscount}
                            input={{
                                id: SearchParam.PromoDiscount,
                            }}
                            isChecked={promoDiscountState}
                            isDisabled={isPromoDiscountDisabled}
                            isOnlyDeselectable={
                                promoDiscountState &&
                                specialMeta?.promoDiscount?.hasCount &&
                                specialMeta?.promoDiscount?.count === 0
                            }
                            onChange={() => {
                                onToggle(SearchParam.PromoDiscount);
                                setPromoDiscountState((current) => !current);
                            }}
                            withUXSelection
                            customClass={{
                                Wrapper: classNames({
                                    [styles.Changed]: isFilterChanged(
                                        SearchParam.PromoDiscount
                                    ),
                                }),
                            }}
                        >
                            <FilterLabel
                                labelText={labelPromoDiscount}
                                tooltip={`<b>${t('TRANS-promotion[i]', {
                                    postProcess: 'interval',
                                    count: 2,
                                })}</b><p>${t(
                                    'TRANS-show-hotels-with-promo-discount-offers'
                                )}</p>`}
                                {...specialMeta?.promoDiscount}
                            />
                        </Checkbox>
                    ),
                    kiFilterData: {
                        label: labelPromoDiscount,
                        isSelectable: !isPromoDiscountDisabled,
                        urlKey: SearchParam.PromoDiscount,
                        value: '1',
                        ...specialMeta?.promoDiscount,
                    },
                },
            ]}
        />
    );
};

export default Special;
